import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import {
  DialogContentText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  StakeActivationData,
  ParsedAccountData
} from '@solana/web3.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { P, match } from 'ts-pattern';
import strings from '../../../localization';
import StakingService, { StakingMode } from '../../../services/stakingService';
import { useConnection } from '../../../utils/connection';
import { useSendTransaction } from '../../../utils/notifications';
import { abbreviateAddress, formatLamports, rewardsFormat } from '../../../utils/utils';
import { useBalanceInfo, useWallet } from '../../../utils/wallet';
import StakingSettingsDialog from '../StakingSettingsDialog';
import UnstakeAlertDialog from './UnstakeAlertDialog';

const ONCHAIN_REWARD_PERCENTAGE = 1.15;
const STAKE_LOCKUP_CUSTODIAN = '11111111111111111111111111111111';

interface ErrorResponse {
  response?: {
    status: number;
  };
}

function is404Error(error: unknown): boolean {
  if (!error) return false;
  
  const errorResponse = error as ErrorResponse;
  return errorResponse.response?.status === 404;
}

function isReallyActive(stakeAccountInfo, detailedAccountInfo) {
  if (stakeAccountInfo?.state === 'inactive') {
    return false;
  }
  
  if (detailedAccountInfo?.parsed?.info?.stake?.delegation) {
    const { activationEpoch, deactivationEpoch } = detailedAccountInfo.parsed.info.stake.delegation;
    
    // 4294967295 (max u32 value, means not deactivated)
    if (deactivationEpoch !== "4294967295" && 
        Number(deactivationEpoch) < Number.MAX_SAFE_INTEGER &&
        stakeAccountInfo?.state === 'active') {
      return false;
    }
  }
  
  return stakeAccountInfo?.state === 'active';
}

function isDeactivatingOrDeactivated(stakeAccountInfo, detailedAccountInfo) {
  if (stakeAccountInfo?.state === 'deactivating' || stakeAccountInfo?.state === 'inactive') {
    return true;
  }
  
  if (detailedAccountInfo?.parsed?.info?.stake?.delegation) {
    const { activationEpoch, deactivationEpoch } = detailedAccountInfo.parsed.info.stake.delegation;
    return deactivationEpoch !== "4294967295" && Number(deactivationEpoch) < Number.MAX_SAFE_INTEGER;
  }
  
  return false;
}

export default function ActiveStakeInfo({
  stakeAccount,
  stakeAccountInfo,
  stakingBackendUrl,
  stakingMode,
}: {
  stakeAccount: PublicKey;
  stakeAccountInfo: StakeActivationData;
  stakingBackendUrl: string;
  stakingMode: StakingMode;
}) {
  const wallet = useWallet();
  const connection = useConnection();
  const queryClient = useQueryClient();
  const stakeAccountBalance = useBalanceInfo(stakeAccount);
  const [sendTransaction, sending] = useSendTransaction();

  const [showAlert, setShowAlert] = useState(false);
  const [isLegacyStake, setIsLegacyStake] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);

  //const TEST_OWNER_AMOUNT = '9pCQD6YNJYaHUb9Cgm4HVbRg55AShqCNnDb5W7t2h6Q6';

  const stakingService = new StakingService(wallet, connection,
    stakingBackendUrl, stakingMode);


  const { data: stakingPlan } = useQuery({
  queryKey: ['stakingPlan', stakingBackendUrl],
  queryFn: async () => {
    return await stakingService.fetchNewStakePlan();
  },
  retry: 5,
  retryDelay: 5000,
  });

  const votePubkey = stakingPlan?.validatorVotePubkey ? 
  new PublicKey(stakingPlan.validatorVotePubkey) : null;

  // Get the detailed account info
  const { data: detailedAccountInfo } = useQuery({
    queryKey: ['detailedAccountInfo', stakeAccount.toBase58()],
    queryFn: async () => {
      try {
        const accountInfo = await connection.getParsedAccountInfo(stakeAccount);
        if (!accountInfo.value) return null;
        const data = accountInfo.value.data as ParsedAccountData;
        if (!data.parsed) return null;
        console.log(data.parsed);
        return {
          parsed: data.parsed,
          owner: accountInfo.value.owner.toBase58()
        };
        
      } catch (error) {
        console.error('Error fetching detailed account info:', error);
        return null;
      }
    },
    enabled: Boolean(stakeAccount),
  });

  const { data: inflationReward } = useQuery({
    queryKey: ['getInflationReward', stakeAccount.toBase58()],
    queryFn: async () => {
      const result = await connection.getInflationReward([stakeAccount]);
      return result?.[0];
    },
    enabled: Boolean(stakeAccountInfo),
    refetchInterval: 60 * 1000,
  });

  const { data: stakingRewards, error: stakingRewardsError } = useQuery({
    queryKey: ['stakingRewards', stakeAccount.toBase58()],
    queryFn: async () => {
      try {
        const response = await stakingService.getStakingRewards(stakeAccount);
        // console.log('getStakingRewards response:', response);
        return response;
      } catch (error) {
        console.error('getStakingRewards failed:', error);
        throw error;
      }
    },
    enabled: Boolean(stakeAccountInfo) && !isLegacyStake,
    refetchInterval: 60 * 1000,
    retry: false,
  });

  const isInitializedNotDelegated = detailedAccountInfo?.parsed.info.type === 'initialized' && 
                                !detailedAccountInfo?.parsed.info.stake;

  const hasValidLockupNotDelegated = detailedAccountInfo?.parsed.info.meta?.lockup && 
                                  !detailedAccountInfo?.parsed.info.stake;

  const handleLegacyUnstake = () => {
    sendTransaction(stakingService.deactivateStake(stakeAccount), {
      onSuccess: refreshStakeAccountInfo,
    });
  };

  const claimPartialStakeReward = async () => {
    try {
      // TODO: Display it
      const result = await stakingService.claimRewards(stakeAccount);
    } catch (error) {
      console.warn(`Failed to claim partial stake rewards: `, error);
    } finally {
      refreshStakeAccountInfo();
    }
  };

  const isLockupExpired = () => {
    if (!detailedAccountInfo?.parsed.info.meta?.lockup) return true;
    
    const lockup = detailedAccountInfo.parsed.info.meta.lockup;
    const currentTimestamp = Math.floor(Date.now() / 1000);
    
    return lockup.unixTimestamp < currentTimestamp && lockup.epoch === 0;
  };

  const refreshStakeAccountInfo = () => {
    queryClient.refetchQueries({
      queryKey: ['stakeAccountInfo', stakeAccount.toBase58()],
    });
    queryClient.refetchQueries({
      queryKey: ['detailedAccountInfo', stakeAccount.toBase58()],
    });
  };

  const handleOnPressUnstake = () => {
    sendTransaction(stakingService.deactivateStake(stakeAccount), {
      onSuccess: refreshStakeAccountInfo,
    });
  };

  const handleOnPressWithdraw = () => {
    sendTransaction(stakingService.withdrawStake(stakeAccount), {
      onSuccess: () => {
        refreshStakeAccountInfo();
      },
    });
  };

  const handleDelegate = () => {
    if (!votePubkey) return;
    
    sendTransaction(stakingService._delegateStake(stakeAccount, votePubkey), {
      onSuccess: refreshStakeAccountInfo,
    });
  };

  const handleReactivateStake = () => {
    if (!votePubkey) return;
    
    sendTransaction(stakingService._delegateStake(stakeAccount, votePubkey), {
      onSuccess: refreshStakeAccountInfo,
    });
  };

  const {
    // totalUnlockedForWithdraw,
    availableForWithdraw = 0,
    totalExpectedReward = 0,
    initialStakeAmount = 0,
    withdrawnAmount = 0,
    minAllowedWithdrawAmount = 0,
    stakeStartedAt = null,
    stakingPeriodSeconds = 0,
  } = stakingRewards || {};

  const totalBalanceExpected = Math.floor(initialStakeAmount * ONCHAIN_REWARD_PERCENTAGE) +
    totalExpectedReward;

  const claimStakeRewardsEnabled = stakingRewards && availableForWithdraw >= minAllowedWithdrawAmount;
  let stakingPeriodUnlocked = false;

  if (stakeStartedAt && stakingPeriodSeconds) {
    const startedDate = Date.parse(stakeStartedAt)

    if (startedDate) {
      const endedDate = startedDate + stakingPeriodSeconds * 1000;
      if (Date.now() >= endedDate) {
        stakingPeriodUnlocked = true;
      }
    }
  }

  const unstakeEnabled = stakingPeriodUnlocked && !sending;

  return (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>{strings.yourStake}</Box>
          <Box>
            <StakingSettingsDialog />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isInitializedNotDelegated && (
          <DialogContentText 
            style={{ 
              color: '#2196f3',
              marginBottom: 16 
            }}
          >
            This stake account is initialized but not currently delegated. You can delegate your stake to start earning rewards.
          </DialogContentText>
        )}

        {isInitializedNotDelegated && (
          <Button
            color="primary"
            onClick={handleDelegate}
            disabled={sending || !votePubkey}
          >
            Delegate Stake
          </Button>
        )}
        {hasValidLockupNotDelegated && (
            <DialogContentText 
              style={{ 
                color: '#2196f3',
                marginBottom: 16 
              }}
            >
              This stake account has a valid lockup period but is not currently delegated. You can delegate your stake to start earning rewards.
            </DialogContentText>
          )}

        {isDeactivatingOrDeactivated(stakeAccountInfo, detailedAccountInfo) && (
          <DialogContentText 
            style={{ 
              color: '#f44336',
              marginBottom: 16,
              textAlign: 'center'
            }}
          >
            {strings.deactiveStakeWarning}
          </DialogContentText>
        )}

        {isDeactivatingOrDeactivated(stakeAccountInfo, detailedAccountInfo) && (
          <Box display="flex" justifyContent="center" width="100%" mb={2}>
            <Button
              color="secondary"
              onClick={handleReactivateStake}
              disabled={sending || !votePubkey}
              style={{ 
                marginBottom: 16,
                backgroundColor: '#e91e63',
                color: 'white'
              }}
            >
              Reactivate Stake
            </Button>
          </Box>
        )}

        <DialogContentText>
          { // @ts-ignore
            match(stakeAccountInfo)
            .with(
              { state: 'activating' },
              () => strings.stakingActivatingDescription,
            )
            .otherwise(() => '')
          }
        </DialogContentText>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableBody>
              {[
                [strings.stakeAccount, abbreviateAddress(stakeAccount)],
                [
                  strings.status,
                  // @ts-ignore
                  match(stakeAccountInfo.state)
                    .with('activating', () => strings.activating)
                    .with('active', () => strings.active)
                    .with('deactivating', () => strings.deactivating)
                    .with('inactive', () => strings.inactive)
                    .exhaustive(),
                ],
                [
                  strings.balance,
                  `${formatLamports(
                    inflationReward?.postBalance ?? stakeAccountBalance?.amount,
                  )} DOMI`,
                ],
                [
                  strings.activeStake,
                  `${formatLamports(stakeAccountInfo?.active)} DOMI`,
                ],
                [
                  strings.inactiveStake,
                  `${formatLamports(stakeAccountInfo?.inactive)} DOMI`,
                ],
              ].map(([name, value, claimButton = false]) => (
                <TableRow>
                  <TableCell component="th" scope="row">
                    {name}
                  </TableCell>
                  <TableCell align="right">
                    <>
                      <b>{value}</b>
                      {claimButton ? (
                        <Button color="primary"
                                disabled={!claimStakeRewardsEnabled}
                                onClick={() => claimPartialStakeReward()}
                        >
                          {strings.claim}
                        </Button>
                      ) : null}
                    </>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        {hasValidLockupNotDelegated && (
            <Button
              color="primary"
              onClick={handleDelegate}
              disabled={sending || !votePubkey}
            >
              Delegate Stake
            </Button>
          )}
        {isLegacyStake && stakeAccountInfo.state === 'active' ? (
          <Button
            color="primary"
            onClick={handleLegacyUnstake}
            disabled={sending}
          >
            Unstake
          </Button>
        ) : (
          match(stakeAccountInfo)
          .with(
            // @ts-ignore
            { state: P.union('activating', 'active') },
            () => (
              <Button
                color="primary"
                onClick={() => setShowAlert(true)}
                disabled={!unstakeEnabled || sending}
              >
                {strings.unstake}
              </Button>
            )
          )
          .with(
            // @ts-ignore
            { state: P.union('deactivating', 'inactive').select() },
            (state: 'deactivating' | 'inactive') => (
              <Button
                color="primary"
                onClick={handleOnPressWithdraw}
                disabled={state === 'deactivating' || sending}
              >
                {strings.withdrawStake}
              </Button>
            )
          )
          .otherwise(() => null)
      )}
    </DialogActions>

      <UnstakeAlertDialog open={showAlert} onConfirm={() => {
        handleOnPressUnstake();
        setShowAlert(false);
      }} onClose={() => setShowAlert(false)} />
    </>
  );
}
